import {
  Box,
  Typography,
  Card,
  Divider,
  Autocomplete,
  Button,
  IconButton,
  CircularProgress,
  InputLabel,
  ToggleButtonGroup,
  Alert,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortItem,
  ptBR,
} from "@mui/x-data-grid";
import {
  TbDownload,
  TbUserCheck,
  TbPlane,
  TbBrush,
  TbUsers,
  TbBuildingStore,
  TbTimeline,
  TbInfoCircle,
} from "react-icons/tb";
import { MdFileDownload } from "react-icons/md";
import { BRIEFING_STATUS_OPTIONS } from "../../utils/briefingStatus";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ReportService from "../../services/report.service";
import { format, differenceInDays, parseISO } from "date-fns";
import ptBRLocale from "date-fns/locale/pt-BR";
import { forceDownload } from "../../utils/download.util";
import ProjectService from "../../services/project.service";
import { useClients } from "../../queries/hooks";
import { AuthContext } from "../../contexts/auth";
import { Input } from "../../components/Input";
import { ViewportAnchoredAutocompletePopper } from "../../components/UI/ViewportAnchoredAutocompletePopper";
import { FaCheck } from "react-icons/fa";

import MuiToggleButton from "@mui/material/ToggleButton";
import { styled } from "@mui/material/styles";

const REPORT_TYPES = [
  {
    value: "dados-cadastrais",
    label: "Dados Cadastrais",
    description:
      "Dados do cliente, solicitante e informações gerais do projeto.",
    icon: TbUserCheck,
    exportKey: "DadosCadastrais",
  },
  {
    value: "aereo",
    label: "Aéreo",
    description: "Informações de voos, trechos, horários e tarifas.",
    icon: TbPlane,
    exportKey: "Aereo",
  },
  {
    value: "criativo",
    label: "Criativo",
    description: "Peças criativas, formatos, veiculações e materiais.",
    icon: TbBrush,
    exportKey: "Criativo",
  },
  {
    value: "rsvp",
    label: "RSVP",
    description: "Lista de convidados, confirmações e status de presença.",
    icon: TbUsers,
    exportKey: "RSVP",
  },
  {
    value: "estande",
    label: "Estande",
    description: "Informações do estande, equipamentos e montagem.",
    icon: TbBuildingStore,
    exportKey: "Estande",
  },
  {
    value: "timeline-projeto",
    label: "Timeline do Projeto",
    description: "Histórico de status, permanência e tempo total dos projetos.",
    icon: TbTimeline,
    exportKey: "TimelineDoProjeto",
  },
];

const StyledToggleButton = styled(MuiToggleButton)({
  "&.Mui-selected, &.Mui-selected:hover": {
    color: "white",
    backgroundColor: "#1976D2",
  },
});

/** Tipos liberados na UI; demais permanecem até validação/produto. */
const REPORT_TYPES_ENABLED = new Set(["dados-cadastrais", "timeline-projeto"]);

export function Reports() {
  const { actionOptions } = useContext(AuthContext);

  const [reportType, setReportType] = useState("dados-cadastrais");
  const isTimelineReport = reportType === "timeline-projeto";

  const [mmCodesFilter, setMMCodesFilter] = useState({
    status: [],
    selectedClients: [],
    initialDate: "",
    finalDate: "",
    filterDateType: "CREATION",
  });
  const [selectedMMCodesArr, setSelectedMMCodesArr] = useState<any[]>([
    { _id: "select-all" },
  ]);

  const [exportName, setExportName] = useState("");
  const [processingReportId, setProcessingReportId] = useState<string | null>(
    null
  );
  const [showLongPeriodWarning, setShowLongPeriodWarning] = useState(false);

  const [sortModel, setSortModel] = useState<GridSortItem[]>([
    { field: "created_at", sort: "desc" },
  ]);

  useEffect(() => {
    const selected = REPORT_TYPES.find((t) => t.value === reportType);
    if (selected) {
      setExportName(
        `Relatorio_${selected.exportKey}_${format(new Date(), "yyyy-MM-dd")}`
      );
    }
  }, [reportType]);

  useEffect(() => {
    if (mmCodesFilter.initialDate && mmCodesFilter.finalDate) {
      try {
        const startDate = parseISO(mmCodesFilter.initialDate);
        const endDate = parseISO(mmCodesFilter.finalDate);
        const daysDifference = differenceInDays(endDate, startDate);

        if (daysDifference > 180) {
          setShowLongPeriodWarning(true);
        } else {
          setShowLongPeriodWarning(false);
        }
      } catch (error) {
        setShowLongPeriodWarning(false);
      }
    } else {
      setShowLongPeriodWarning(false);
    }
  }, [mmCodesFilter.initialDate, mmCodesFilter.finalDate]);

  const { data: mmCodes } = useQuery({
    queryKey: ["get-mm-codes", mmCodesFilter],
    queryFn: () => ProjectService.getMMCodes(mmCodesFilter),
  });

  const queryClient = useQueryClient();
  const { data: reports } = useQuery("reports", ReportService.getAll, {
    refetchInterval: processingReportId ? 3000 : false,
  });
  const { data: clients } = useClients();

  const [hasShownSuccessToast, setHasShownSuccessToast] = useState(false);

  useEffect(() => {
    if (processingReportId && reports) {
      const report = (reports as any[]).find(
        (r: any) => r._id === processingReportId
      );
      if (report?.processing_status === "completed") {
        setProcessingReportId(null);
        if (!hasShownSuccessToast) {
          setHasShownSuccessToast(true);
          toast.success("Relatório gerado com sucesso!");
        }
      }
    }
  }, [reports, processingReportId, hasShownSuccessToast]);

  const { mutate: createReport, isLoading: isLoadingCreate } = useMutation(
    (data: any) => ReportService.create(data),
    {
      onSuccess: (response) => {
        toast.success("Relatório em processamento...");
        setProcessingReportId(response._id);
        setHasShownSuccessToast(false);

        const selected = REPORT_TYPES.find((t) => t.value === reportType);
        if (selected) {
          setExportName(
            `Relatorio_${selected.exportKey}_${format(
              new Date(),
              "yyyy-MM-dd"
            )}`
          );
        }

        queryClient.invalidateQueries("reports");
      },
    }
  );

  const columns: GridColDef[] = [
    {
      field: "report_name",
      flex: 1,
      headerName: "Nome do relatório",
    },
    {
      field: "user",
      flex: 1,
      headerName: "Solicitante",
      valueGetter: (params) => params.value?.name || "",
    },
    {
      field: "created_at",
      flex: 1,
      headerName: "Data Exportado",
      renderCell: (params) =>
        params.value
          ? format(new Date(params.value), "dd/MM/yyyy HH:mm:ss", {
              locale: ptBRLocale,
            })
          : "",
    },
    {
      field: "action",
      headerName: "",
      width: 60,
      renderCell: (params: GridRenderCellParams) => (
        <Box border={"1px solid #1976D2"} borderRadius={1.5}>
          <IconButton
            aria-label="download"
            size="small"
            onClick={() => downloadReport(params.row._id)}
          >
            <TbDownload size={14} color="#1976D2" />
          </IconButton>
        </Box>
      ),
    },
  ];

  async function downloadReport(id: string) {
    const response = await ReportService.download(id);
    if (!!response.file_url) {
      forceDownload(response.file_url, response.report_name, "xlsx");
    }
  }

  async function deleteReport(id: string) {
    await ReportService.delete(id);
    toast.success("Relatório deletado com sucesso!");
    queryClient.invalidateQueries("reports");
  }

  async function createReportHandler() {
    if (!exportName) return toast.warn("Informe um nome para o relatório");
    if (!mmCodesFilter.initialDate || !mmCodesFilter.finalDate)
      return toast.warn("Selecione o período (data de início e fim)");
    if (selectedMMCodesArr.length < 1)
      return toast.warn("Selecione pelo menos um código MM");

    const payload: any = {
      reportName: exportName,
      reportType, // Tipo do relatório: dados-cadastrais, aereo, criativo, rsvp, estande
      mmCodes: selectedMMCodesArr,
    };
    if (
      selectedMMCodesArr.length === 1 &&
      selectedMMCodesArr[0]._id === "select-all"
    ) {
      if (!mmCodes || mmCodes.length === 0) {
        return toast.warn("Aguarde o carregamento dos códigos MM");
      }
      payload.mmCodes = mmCodes.filter((el: any) => el._id !== "select-all");
    }
    payload.mmCodes = payload.mmCodes.map((el: any) => el.mmCode);

    createReport(payload);
  }

  function handleSelectMMCode(e: any, newValue: any | null) {
    if (newValue.length < 1) {
      setSelectedMMCodesArr([{ _id: "select-all" }]);
    } else {
      setSelectedMMCodesArr(
        newValue.filter((el: any) => el._id !== "select-all")
      );
    }
  }

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      flex={1}
      py={4}
      px={3}
      width={"80%"}
      sx={{
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      <Typography variant={"h5"} mb={3}>
        Relatórios
      </Typography>

      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "visible",
          boxShadow: "none",
          backgroundColor: "transparent",
          gap: "20px",
        }}
      >
        {/* Secao 1 - Tipo de relatorio */}
        <Box
          px={3}
          pt={3}
          pb={2}
          bgcolor={"#FEFDFD"}
          sx={{ boxShadow: "0px 4px 18px 0px #4B465C1A", borderRadius: "10px" }}
        >
          <Typography variant="body1" fontWeight={500} mb={2}>
            <Typography component="span" variant="body1" fontWeight={700}>
              1. Tipo de relatório:
            </Typography>{" "}
            <Typography component="span" variant="body2" color="text.secondary">
              (selecione a guia que deseja exportar)
            </Typography>
          </Typography>

          <Box display={"flex"} flexDirection={"row"} flexWrap="wrap" gap={1.5}>
            {REPORT_TYPES.map((tipo) => {
              const Icon = tipo.icon;
              const isSelected = reportType === tipo.value;
              const isDisabled = !REPORT_TYPES_ENABLED.has(tipo.value);
              return (
                <Box
                  key={tipo.value}
                  onClick={() => {
                    if (isDisabled) return;
                    setReportType(tipo.value);
                    if (tipo.value === "timeline-projeto") {
                      setMMCodesFilter((prev) => ({ ...prev, status: [] }));
                    }
                  }}
                  sx={{
                    flex: "1 1 140px",
                    minWidth: 140,
                    border: isSelected
                      ? "2px solid #1976D2"
                      : "1px solid #E0E0E0",
                    borderRadius: 2,
                    px: 1.5,
                    py: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    position: "relative",
                    bgcolor: isDisabled
                      ? "#F5F5F5"
                      : isSelected
                      ? "#EEF6FE"
                      : "#fff",
                    opacity: isDisabled ? 0.5 : 1,
                    transition: "border-color 0.15s",
                    "&:hover": {
                      borderColor: isDisabled ? "#E0E0E0" : "#1976D2",
                    },
                  }}
                >
                  {isSelected && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 20,
                        height: 20,
                        bgcolor: "#1976D2",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaCheck size={9} color="#fff" />
                    </Box>
                  )}

                  <Icon
                    size={36}
                    color={
                      isDisabled
                        ? "#BDBDBD"
                        : isSelected
                        ? "#1976D2"
                        : "#9E9E9E"
                    }
                    style={{ marginBottom: 10 }}
                  />

                  <Typography
                    fontWeight={700}
                    color={
                      isDisabled
                        ? "#BDBDBD"
                        : isSelected
                        ? "#1976D2"
                        : "text.primary"
                    }
                    sx={{
                      mb: 0.5,
                      fontSize: "0.8125rem",
                      lineHeight: 1.3,
                      minHeight: "2.1em",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {tipo.label}
                  </Typography>

                  <Typography
                    color={isDisabled ? "#D0D0D0" : "text.secondary"}
                    sx={{
                      fontSize: "0.6875rem",
                      lineHeight: 1.4,
                    }}
                  >
                    {tipo.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Secao 2 - Filtros */}
        <Box
          px={3}
          pt={2.5}
          pb={2}
          bgcolor={"#FEFDFD"}
          sx={{ boxShadow: "0px 4px 18px 0px #4B465C1A", borderRadius: "10px" }}
        >
          <Typography variant="body1" fontWeight={500} mb={2}>
            <Typography component="span" variant="body1" fontWeight={700}>
              2. Filtros:
            </Typography>{" "}
            <Typography component="span" variant="body2" color="text.secondary">
              Refine sua exportação
            </Typography>
          </Typography>

          <Box display={"flex"} flexDirection={"column"} gap={1.5}>
            {/* Linha 1 - Clientes e Status */}
            <Box
              display={"grid"}
              gridTemplateColumns={isTimelineReport ? "1fr" : "1fr 1fr"}
              gap={2}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Clientes
                </Typography>
                <Autocomplete
                  options={clients || []}
                  PopperComponent={ViewportAnchoredAutocompletePopper}
                  getOptionLabel={(option) =>
                    `${option.handle_id} - ${option.name}` || ""
                  }
                  fullWidth
                  size="small"
                  multiple
                  renderInput={(params) => (
                    <Input
                      {...params}
                      placeholder="Selecionar clientes"
                      fullWidth
                      marginTop={0}
                    />
                  )}
                  onChange={(e, newValue: any | null) =>
                    newValue !== null
                      ? setMMCodesFilter({
                          ...mmCodesFilter,
                          selectedClients: newValue.map((el: any) => el._id),
                        })
                      : ""
                  }
                />
              </Box>

              {!isTimelineReport && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={0.5}
                  >
                    Status
                  </Typography>
                  <Autocomplete
                    options={BRIEFING_STATUS_OPTIONS}
                    PopperComponent={ViewportAnchoredAutocompletePopper}
                    getOptionLabel={(option) => option.text || ""}
                    fullWidth
                    size="small"
                    multiple
                    renderInput={(params) => (
                      <Input
                        {...params}
                        placeholder="Selecionar status"
                        fullWidth
                        marginTop={0}
                      />
                    )}
                    onChange={(e, newValue: any | null) =>
                      newValue !== null
                        ? setMMCodesFilter({
                            ...mmCodesFilter,
                            status: newValue.map((el: any) => el.value),
                          })
                        : ""
                    }
                  />
                </Box>
              )}
            </Box>

            {/* Linha 2 - Filtrar por e datas */}
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-end"}
              gap={2}
            >
              <Box>
                <InputLabel
                  sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}
                >
                  Filtrar por:
                </InputLabel>
                <ToggleButtonGroup
                  size="small"
                  color="primary"
                  value={mmCodesFilter.filterDateType}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      setMMCodesFilter({
                        ...mmCodesFilter,
                        filterDateType: newValue,
                      });
                    }
                  }}
                  exclusive
                  aria-label="filtrar-por"
                >
                  <StyledToggleButton
                    style={{ textTransform: "capitalize" }}
                    value="CREATION"
                  >
                    {mmCodesFilter.filterDateType === "CREATION" && (
                      <FaCheck style={{ marginRight: 4 }} />
                    )}
                    Data de Criação
                  </StyledToggleButton>
                  <StyledToggleButton
                    style={{ textTransform: "capitalize" }}
                    value="EVENT"
                  >
                    {mmCodesFilter.filterDateType === "EVENT" && (
                      <FaCheck style={{ marginRight: 4 }} />
                    )}
                    Data do Evento
                  </StyledToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Início
                </Typography>
                <Input
                  size="small"
                  type="date"
                  marginTop={0}
                  sx={{ maxWidth: 180 }}
                  InputLabelProps={{ shrink: true }}
                  value={mmCodesFilter.initialDate}
                  onChange={(e) =>
                    setMMCodesFilter({
                      ...mmCodesFilter,
                      initialDate: e.target.value,
                    })
                  }
                />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Fim
                </Typography>
                <Input
                  size="small"
                  type="date"
                  marginTop={0}
                  sx={{ maxWidth: 180 }}
                  InputLabelProps={{ shrink: true }}
                  value={mmCodesFilter.finalDate}
                  onChange={(e) =>
                    setMMCodesFilter({
                      ...mmCodesFilter,
                      finalDate: e.target.value,
                    })
                  }
                />
              </Box>
            </Box>

            {/* Mensagem de período */}
            {showLongPeriodWarning && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Relatórios com períodos superiores a 180 dias podem levar alguns
                minutos para serem processados.
              </Alert>
            )}

            {/* Mensagem de aviso quando período não está preenchido */}
            {(!mmCodesFilter.initialDate || !mmCodesFilter.finalDate) && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                É obrigatório selecionar o período (data de início e fim) para
                gerar o relatório.
              </Alert>
            )}

            {/* Linha 3 - Codigo MM */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.5}
              >
                Código MM
              </Typography>
              <Autocomplete
                options={!!mmCodes ? [{ _id: "select-all" }, ...mmCodes] : []}
                PopperComponent={ViewportAnchoredAutocompletePopper}
                multiple
                getOptionLabel={(option: any) =>
                  option._id === "select-all"
                    ? "Selecionar Todos"
                    : `${option.mmCode}/${option.yearCode} - ${option.event}` ||
                      ""
                }
                renderOption={(props, option: any) => (
                  <li {...props} key={option._id}>
                    {option._id === "select-all"
                      ? "Selecionar Todos"
                      : `${option.mmCode}/${option.yearCode} - ${option.event}` ||
                        ""}
                  </li>
                )}
                fullWidth
                size="small"
                renderInput={(params) => (
                  <Input
                    {...params}
                    placeholder="Selecionar todos"
                    fullWidth
                    marginTop={0}
                  />
                )}
                onChange={handleSelectMMCode}
                value={selectedMMCodesArr}
              />
            </Box>

            {/* Linha 4 - Nome da exportacao + botao */}
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-end"}
              gap={1.5}
            >
              <Box flex={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Nome da exportação
                </Typography>
                <Input
                  fullWidth
                  size="small"
                  marginTop={0}
                  value={exportName}
                  onChange={(e) => setExportName(e.target.value)}
                  disabled
                />
              </Box>

              <Button
                disabled={
                  isLoadingCreate ||
                  processingReportId !== null ||
                  !actionOptions["createRelatórios"] ||
                  !mmCodesFilter.initialDate ||
                  !mmCodesFilter.finalDate
                }
                variant="contained"
                size="medium"
                onClick={createReportHandler}
                startIcon={
                  isLoadingCreate || processingReportId !== null ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <MdFileDownload size={18} />
                  )
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2.5,
                  whiteSpace: "nowrap",
                  mb: 0,
                }}
              >
                {isLoadingCreate || processingReportId !== null
                  ? "Processando..."
                  : "Exportar"}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Secao 3 - Gerenciar exportacoes */}
        <Box
          px={3}
          pt={2.5}
          pb={3}
          bgcolor={"#FEFDFD"}
          sx={{ boxShadow: "0px 4px 18px 0px #4B465C1A", borderRadius: "10px" }}
        >
          <Typography
            component="span"
            variant="body1"
            fontWeight={700}
            mb={2}
            display="block"
          >
            3. Gerenciar exportações
          </Typography>
          <Box sx={{ minHeight: 250 }}>
            <DataGrid
              autoHeight
              rows={
                (reports as any[])?.filter(
                  (r: any) => r.processing_status === "completed"
                ) || []
              }
              columns={columns}
              getRowId={(row) => row._id}
              pageSize={10}
              rowsPerPageOptions={[10]}
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "#FAFAFA",
                },
              }}
            />
          </Box>
        </Box>
      </Card>

      {/* Nota de rodape */}
      <Box
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        gap={0.75}
        mt={2}
        px={3}
        pt={2.5}
        pb={3}
        bgcolor={"#EDF4FE"}
        sx={{ boxShadow: "0px 4px 18px 0px #4B465C1A", borderRadius: "10px" }}
      >
        <TbInfoCircle size={16} color="#1976D2" />
        <Typography variant="caption" color="#1976D2">
          {isTimelineReport
            ? "O relatório apresenta cada projeto em uma única linha. A quantidade de colunas da Timeline é definida de acordo com o maior número de ocorrências do lote."
            : "Cada relatório será gerado em um arquivo XLS individual, contendo apenas os dados da guia selecionada."}
        </Typography>
      </Box>
    </Box>
  );
}
